import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, Button, IconButton, Tooltip, Skeleton,
  Paper, TextField, InputAdornment, Collapse, Divider,
} from '@mui/material';
import ArrowBackIcon         from '@mui/icons-material/ArrowBack';
import PlayArrowIcon         from '@mui/icons-material/PlayArrow';
import SaveOutlinedIcon      from '@mui/icons-material/SaveOutlined';
import SearchIcon            from '@mui/icons-material/Search';
import ClearIcon             from '@mui/icons-material/Clear';
import GroupsIcon            from '@mui/icons-material/Groups';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';
import AutoAwesomeIcon       from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSnackbar } from 'notistack';
import { useApp } from '../context/AppContext';
import {
  fetchRecord, fetchRecords, createRecord,
  executePayrollRun, approvePayrollRun,
} from '../services/api';

const fmtINR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};
const fmtCompact = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  if (Math.abs(num) >= 10000000) return (num / 10000000).toFixed(2) + ' Cr';
  if (Math.abs(num) >= 100000)   return (num / 100000).toFixed(2) + ' L';
  if (Math.abs(num) >= 1000)     return (num / 1000).toFixed(1) + ' K';
  return String(num);
};

// Mirror backend classification logic
const classify = (elementType) => {
  const t = String(elementType || '').toLowerCase();
  if (t.includes('deduction') || t === 'tax' || t.includes('loan')) return 'Deduction';
  if (t.includes('employer')) return 'Employer Contribution';
  return 'Earning';
};

export default function ProcessPayRunPage({ periodId, existingRunId, onBack, onDone }) {
  const { enqueueSnackbar } = useSnackbar();
  const { state: appState } = useApp();
  const activeContext = appState?.activeContext || null;

  // Loaded data
  const [period, setPeriod]                 = useState(null);
  const [payroll, setPayroll]               = useState(null);
  const [assignments, setAssignments]       = useState([]);
  const [persons, setPersons]               = useState([]);
  const [elements, setElements]             = useState([]);
  const [elementEntries, setElementEntries] = useState([]);
  const [esiAccounts, setEsiAccounts]       = useState([]);
  const [existingRun, setExistingRun]       = useState(null);
  const [allRuns, setAllRuns]               = useState([]);
  const [attendances, setAttendances]       = useState([]);
  const [holidays, setHolidays]             = useState([]);
  const [advanceSalaries, setAdvanceSalaries] = useState([]);
  const [payrollRels, setPayrollRels]         = useState([]);
  const [ffRecoverySchedules, setFfRecoverySchedules] = useState([]);
  const [fullFinalSettlements, setFullFinalSettlements] = useState([]);
  const [ffRecoveries, setFfRecoveries] = useState([]);
  const [loading, setLoading]                 = useState(true);

  // Form state
  const [workingDays, setWorkingDays]           = useState(26);
  const [calendarDays, setCalendarDays]         = useState(31);
  const [scheduledDays, setScheduledDays]       = useState(26);
  const [weeklyHolidayDays, setWeeklyHolidayDays] = useState(5);
  const [publicHolidayDays, setPublicHolidayDays] = useState(0);
  const [lopByEmp, setLopByEmp]         = useState({}); // { assignment_id: lop_days }
  const [excludedEmps, setExcludedEmps] = useState(new Set());
  const [expandedEmp, setExpandedEmp]   = useState(null);
  const [search, setSearch]             = useState('');
  const [bulkLop, setBulkLop]           = useState('');
  const [busy, setBusy]                 = useState(null);
  // Phase 6: Variable inputs per employee — Overtime, Bonus, Ad-hoc
  // { assignment_id: { ot_hours, ot_multiplier, bonus, ad_hoc_earning, ad_hoc_deduction } }
  const [varInputs, setVarInputs]       = useState({});
  const setVarField = (asnId, field, value) => {
    setVarInputs(prev => ({
      ...prev,
      [asnId]: { ...(prev[asnId] || {}), [field]: value },
    }));
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchRecord('PAY_PERIOD', periodId).catch(() => null),
      fetchRecords('PAYROLL'),
      fetchRecords('ASSIGNMENT'),
      fetchRecords('PERSON'),
      fetchRecords('ELEMENT_TYPE'),
      fetchRecords('ELEMENT_ENTRY'),
      fetchRecords('ESI_ACCOUNT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAYROLL_RUN'),
      existingRunId ? fetchRecord('PAYROLL_RUN', existingRunId).catch(() => null) : Promise.resolve(null),
      fetchRecords('ATTENDANCE').catch(() => ({ data: { data: [] } })),
      fetchRecords('HOLIDAY_CALENDAR').catch(() => ({ data: { data: [] } })),
      fetchRecords('ADVANCE_SALARY').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAYROLL_RELATIONSHIP').catch(() => ({ data: { data: [] } })),
      fetchRecords('FF_RECOVERY_SCHEDULE').catch(() => ({ data: { data: [] } })),
      fetchRecords('FULL_FINAL_SETTLEMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('FF_RECOVERY').catch(() => ({ data: { data: [] } })),
    ]).then(([per, prs, asn, pers, els, ee, esi, runs, exRun, attReq, holReq, advReq, prelReq, ffReq, ffsReq, ffrReq]) => {
      const periodData = per?.data?.data || null;
      setPeriod(periodData);
      setPayroll((prs.data?.data || []).find(p => p.payroll_id === periodData?.payroll_id) || null);
      setAssignments(asn.data?.data || []);
      setPersons(pers.data?.data || []);
      setElements(els.data?.data || []);
      setElementEntries(ee.data?.data || []);
      setEsiAccounts(esi?.data?.data || []);
      setAllRuns(runs.data?.data || []);
      setAttendances(attReq.data?.data || []);
      setHolidays(holReq.data?.data || []);
      setAdvanceSalaries(advReq?.data?.data || []);
      setPayrollRels(prelReq?.data?.data || []);
      setFfRecoverySchedules(ffReq.data?.data || []);
      setFullFinalSettlements(ffsReq?.data?.data || []);
      setFfRecoveries(ffrReq?.data?.data || []);
      if (exRun?.data?.data) setExistingRun(exRun.data.data);
      setLoading(false);
    });
  }, [periodId, existingRunId]);

  // Phase 6: Auto-calculate Working Days, LOP, and OT based on Attendance
  useEffect(() => {
    if (loading || !period) return;
    
    const start = new Date(period.start_date);
    const end = new Date(period.end_date || period.start_date);
    const diffTime = Math.abs(end - start);
    const totalCalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    setCalendarDays(totalCalDays);

    const DAY_MAP = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
    const workingDaysConfig = payroll?.working_days;

    let calcScheduledDays = 0;
    let calcWeeklyHolidays = 0;
    let workingIndexes = [];

    if (workingDaysConfig && Array.isArray(workingDaysConfig) && workingDaysConfig.length > 0) {
      workingIndexes = workingDaysConfig.map(d => DAY_MAP[d?.toUpperCase()]).filter(i => i !== undefined);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (workingIndexes.includes(d.getDay())) calcScheduledDays++;
        else calcWeeklyHolidays++;
      }
    } else {
      workingIndexes = [1, 2, 3, 4, 5]; // Default Mon-Fri
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (d.getDay() !== 0 && d.getDay() !== 6) calcScheduledDays++;
        else calcWeeklyHolidays++;
      }
    }

    // Calculate Active Holidays within period that land on a Scheduled Working Day
    const hols = holidays.filter(h => {
      if (h.active_flag !== 'Y' && h.active_flag !== 'Active' && h.active_flag !== true) return false;
      const hd = new Date(h.holiday_date);
      if (hd < start || hd > end) return false;
      return workingIndexes.includes(hd.getDay());
    });

    const calcWorkingDays = Math.max(1, calcScheduledDays - hols.length);
    setScheduledDays(calcScheduledDays);
    setWeeklyHolidayDays(calcWeeklyHolidays);
    setPublicHolidayDays(hols.length);
    setWorkingDays(calcWorkingDays);

    // Filter Attendance for this period
    const attInPeriod = attendances.filter(a => {
      const ad = new Date(a.attendance_date);
      return ad >= start && ad <= end;
    });

    const autoLop = {};
    const autoVars = { ...varInputs };

    const activeAssignments = assignments.filter(a => {
      if (a.payroll_id !== period.payroll_id || a.active_flag !== 'Y') return false;
      const rel = payrollRels.find(r => r.assignment_id === a.assignment_id);
      if (rel) {
        if (rel.start_date && period.end_date && rel.start_date > period.end_date) return false;
        if (rel.end_date && period.start_date && rel.end_date < period.start_date) return false;
      }
      return true;
    });
    
    activeAssignments.forEach(a => {
      const myAtt = attInPeriod.filter(att => att.assignment_id === a.assignment_id);
      
      let presentOnWorkingDays = 0;
      let presentOnHolidays = 0;
      let paidLeaves = 0;
      let otHours = 0;

      myAtt.forEach(att => {
        const s = (att.attendance_status || '').toLowerCase();
        const attDate = new Date(att.attendance_date);
        const dayIdx = attDate.getDay();
        let isWeeklyHoliday = false;
        if (workingIndexes.length > 0) {
          isWeeklyHoliday = !workingIndexes.includes(dayIdx);
        } else {
          isWeeklyHoliday = (dayIdx === 0 || dayIdx === 6);
        }

        if (s.includes('present') || s.includes('half')) {
          if (isWeeklyHoliday) presentOnHolidays += 1;
          else presentOnWorkingDays += 1;
        }
        if (s.includes('leave')) paidLeaves += 1;
        otHours += (Number(att.ot_hours) || 0);
      });

      // LOP = ScheduledWorkingDays - Present - PaidLeaves
      let lop = calcWorkingDays - presentOnWorkingDays - paidLeaves;
      if (lop < 0) lop = 0;
      autoLop[a.assignment_id] = lop;

      if (otHours > 0 || (autoVars[a.assignment_id] && autoVars[a.assignment_id].ot_hours)) {
         autoVars[a.assignment_id] = {
           ...(autoVars[a.assignment_id] || {}),
           ot_hours: otHours,
           extra_working_days: presentOnHolidays,
         };
      } else if (presentOnHolidays > 0) {
        autoVars[a.assignment_id] = {
          ...(autoVars[a.assignment_id] || {}),
          extra_working_days: presentOnHolidays,
        };
      }
    });

    setLopByEmp(prev => ({ ...autoLop, ...prev })); // only set once, manual overrides take precedence later
    setVarInputs(prev => ({ ...autoVars, ...prev }));

  }, [loading, period, payroll]); // only run when loaded

  const elementMap = useMemo(() =>
    Object.fromEntries(elements.map(e => [e.element_id, e])), [elements]);
  const personMap = useMemo(() =>
    Object.fromEntries(persons.map(p => [p.person_id, p])), [persons]);

  // Next available run_id (continues RUN02, RUN03 pattern with pad=2)
  const nextRunId = useMemo(() => {
    if (existingRun) return existingRun.run_id;
    const max = allRuns.reduce((m, r) => {
      const n = parseInt(String(r.run_id || '').replace('RUN', ''), 10);
      return Number.isFinite(n) ? Math.max(m, n) : m;
    }, 0);
    return 'RUN' + String(max + 1).padStart(2, '0');
  }, [allRuns, existingRun]);

  // ── Live employee calculation ──────────────────────────────────────────
  const employees = useMemo(() => {
    if (!period) return [];
    return assignments
      .filter(a => {
        if (a.payroll_id !== period.payroll_id || a.active_flag !== 'Y') return false;
        
        const ffs = fullFinalSettlements.find(f => f.assignment_id === a.assignment_id && ['Pending', 'Processing', 'Approved'].includes(f.status));
        if (ffs && ffs.linked_payroll_run_id === period.period_id) return true;

        const rel = payrollRels.find(r => r.assignment_id === a.assignment_id);
        if (rel) {
          if (rel.start_date && period.end_date && rel.start_date > period.end_date) return false;
          if (rel.end_date && period.start_date && rel.end_date < period.start_date) return false;
        }
        return true;
      })
      .map(a => {
        const activeEntries = elementEntries.filter(e => {
          if (e.assignment_id !== a.assignment_id) return false;
          const eFrom = e.effective_from || '1900-01-01';
          const eTo = e.effective_to || '2099-12-31';
          return eFrom <= period.end_date && eTo >= period.start_date;
        });

        // Mirror backend logic: process active non-template entries first, then template entries, discarding template if override exists.
        const activeNonTemplate = activeEntries.filter(e => e.source_type !== 'Template');
        const activeTemplate = activeEntries.filter(e => e.source_type === 'Template');
        
        const finalEntriesMap = {};
        const overrideElementIds = new Set();
        
        // 1. Get latest active non-template entries
        for (const e of activeNonTemplate) {
          const current = finalEntriesMap[e.element_id];
          const currDate = current ? (current.effective_from || '1900-01-01') : '1900-01-01';
          const newDate = e.effective_from || '1900-01-01';
          if (!current || newDate > currDate) {
            finalEntriesMap[e.element_id] = e;
          }
        }
        
        // Note overrides
        for (const e of Object.values(finalEntriesMap)) {
          if (e.override_flag) {
            overrideElementIds.add(e.element_id);
          }
        }
        
        // 2. Get latest active template entries
        const latestTemplateMap = {};
        for (const e of activeTemplate) {
          const current = latestTemplateMap[e.element_id];
          const currDate = current ? (current.effective_from || '1900-01-01') : '1900-01-01';
          const newDate = e.effective_from || '1900-01-01';
          if (!current || newDate > currDate) {
            latestTemplateMap[e.element_id] = e;
          }
        }
        
        // 3. Combine, discarding template if override exists
        for (const e of Object.values(latestTemplateMap)) {
          if (!overrideElementIds.has(e.element_id)) {
            finalEntriesMap[e.element_id] = e;
          }
        }
        
        const entries = Object.values(finalEntriesMap);

        const lopDays  = Math.max(0, Math.min(workingDays, Number(lopByEmp[a.assignment_id]) || 0));
        const excluded = excludedEmps.has(a.assignment_id);

        const earnings   = [];
        const deductions = [];
        let gross = 0, totalDed = 0, employerCont = 0;
        let baseGrossForOT = 0;

        const ffs = fullFinalSettlements.find(f => f.assignment_id === a.assignment_id && ['Pending', 'Processing', 'Approved'].includes(f.status));
        
        const activeLoans = ffs ? [] : advanceSalaries.filter(l => l.assignment_id === a.assignment_id && l.loan_status === 'ACTIVE' && Number(l.outstanding) > 0);
        let activeAsdDeduction = 0;
        for (const loan of activeLoans) {
          activeAsdDeduction += Math.min(Number(loan.emi_amount) || 0, Number(loan.outstanding) || 0);
        }

        // F&F Recoveries
        if (ffs) {
          const recoveries = ffRecoveries.filter(r => r.ff_id === ffs.ff_id && ['Payroll', 'Mixed'].includes(r.recovery_method));
          for (const rec of recoveries) {
            activeAsdDeduction += Number(rec.remaining_amount) || 0;
          }
        }

        for (const entry of entries) {
          const element = elementMap[entry.element_id] || {};
          const cls     = classify(element.type);
          let rawAmt  = Number(entry.pay_value) || 0;
          
          if (element.element_code === 'ASD' || element.element_name === 'Advance Salary Deduction') {
            rawAmt = activeAsdDeduction;
          }

          if (cls === 'Earning') baseGrossForOT += rawAmt;

          // No pro-rata on standard earnings; LOP will be applied as explicit deduction
          const factor  = 1;
          const amount  = Math.round(rawAmt * factor * 100) / 100;
          const item    = {
            element_id: entry.element_id,
            element_name: element.element_name || entry.element_id,
            raw_amount: rawAmt,
            amount,
            classification: cls,
          };
          if (cls === 'Deduction')                  { deductions.push(item); totalDed += Math.abs(amount); }
          else if (cls === 'Employer Contribution') { employerCont += amount; }
          else                                      { earnings.push(item); gross += amount; }
        }

        // Apply Variable Inputs
        const v = varInputs[a.assignment_id] || {};
        const otHours = Number(v.ot_hours) || 0;
        const otMultiplier = Number(v.ot_multiplier) || 1.5;
        const bonus = Number(v.bonus) || 0;
        const adHocEarn = Number(v.ad_hoc_earning) || 0;
        const adHocDed = Number(v.ad_hoc_deduction) || 0;
        const extraWorkingDays = Number(v.extra_working_days) || 0;

        // OT Amount Calculation: (Gross / WorkingDays / 8) * OTHours * Multiplier
        if (otHours > 0 && workingDays > 0) {
          const hourlyRate = baseGrossForOT / workingDays / 8;
          const otAmount = Math.round(hourlyRate * otHours * otMultiplier * 100) / 100;
          if (otAmount > 0) {
            earnings.push({ element_id: 'OT_AMOUNT', element_name: 'Overtime Amount', raw_amount: otAmount, amount: otAmount, classification: 'Earning' });
            gross += otAmount;
          }
        }

        // Holiday Worked Pay: extra day pay per holiday worked
        let holidayPay = 0;
        if (extraWorkingDays > 0 && workingDays > 0) {
          const perDayRate = baseGrossForOT / workingDays;
          holidayPay = Math.round(perDayRate * extraWorkingDays * 100) / 100;
          if (holidayPay > 0) {
            earnings.push({ element_id: 'HOLIDAY_PAY', element_name: `Extra Working Day Pay (${extraWorkingDays}d)`, raw_amount: holidayPay, amount: holidayPay, classification: 'Earning' });
            gross += holidayPay;
          }
        }

        if (bonus > 0) {
          earnings.push({ element_id: 'BONUS', element_name: 'Bonus', raw_amount: bonus, amount: bonus, classification: 'Earning' });
          gross += bonus;
        }

        if (adHocEarn > 0) {
          earnings.push({ element_id: 'AD_HOC_EARN', element_name: 'Ad-hoc Earning', raw_amount: adHocEarn, amount: adHocEarn, classification: 'Earning' });
          gross += adHocEarn;
        }

        if (adHocDed > 0) {
          deductions.push({ element_id: 'AD_HOC_DED', element_name: 'Ad-hoc Deduction', raw_amount: adHocDed, amount: adHocDed, classification: 'Deduction' });
          totalDed += adHocDed;
        }

        // LOP Deduction logic
        // Formula: Per Day Salary = Gross / Working Days
        // LOP Deduction = Per Day Salary * LOP Days
        if (lopDays > 0 && workingDays > 0) {
          const perDaySalary = baseGrossForOT / workingDays;
          const lopDeduction = Math.round(perDaySalary * lopDays * 100) / 100;
          if (lopDeduction > 0) {
            deductions.push({ element_id: 'LOP_DEDUCTION', element_name: 'LOP Deduction', raw_amount: lopDeduction, amount: lopDeduction, classification: 'Deduction' });
            totalDed += lopDeduction;
          }
        }

        // Dynamic ESI Calculation
        const esiElement = elements.find(e => (e.element_code === 'EMPLOYEE_STATE_INSURANCE' || e.element_name === 'EMPLOYEE_STATE_INSURANCE') && e.COMPANY_id === a.COMPANY_id);
        if (esiElement) {
          const esiAcc = esiAccounts.find(ea => ea.assignment_id === a.assignment_id && ea.account_status === 'ACTIVE');
          const esiCeiling = esiAcc ? (esiAcc.gross_ceiling || 21000) : 21000;
          const standardGross = baseGrossForOT;
          
          let isEligible = standardGross > 0 && standardGross <= esiCeiling;
          
          let lopDedAmt = 0;
          if (lopDays > 0 && workingDays > 0) {
            lopDedAmt = baseGrossForOT / workingDays * lopDays;
          }
          const proratedGross = standardGross - lopDedAmt;
          
          const esiIdx = deductions.findIndex(d => d.element_id === esiElement.element_id);
          
          if (!isEligible) {
            if (esiIdx !== -1) {
              totalDed -= Math.abs(deductions[esiIdx].amount);
              deductions[esiIdx].amount = 0;
              deductions[esiIdx].raw_amount = 0;
            }
          } else {
            const pctEe = esiAcc ? (esiAcc.esi_ee_percentage || 0.75) : 0.75;
            const pctEr = esiAcc ? (esiAcc.esi_er_percentage || 3.25) : 3.25;
            const dynamicEsiVal = Math.ceil(proratedGross * (pctEe / 100));
            const employerEsiVal = Math.ceil(proratedGross * (pctEr / 100));
            
            if (esiIdx !== -1) {
              totalDed -= Math.abs(deductions[esiIdx].amount);
              deductions[esiIdx].amount = dynamicEsiVal;
              deductions[esiIdx].raw_amount = dynamicEsiVal;
              totalDed += dynamicEsiVal;
            } else {
              deductions.push({ element_id: esiElement.element_id, element_name: esiElement.element_name || 'EMPLOYEE_STATE_INSURANCE', raw_amount: dynamicEsiVal, amount: dynamicEsiVal, classification: 'Deduction' });
              totalDed += dynamicEsiVal;
            }

            // Track Employer Contribution
            employerCont += employerEsiVal;
          }
        }

        const person   = personMap[a.person_id];
        const fullName = person ? [person.first_name, person.last_name].filter(Boolean).join(' ') : null;

        return {
          assignment_id: a.assignment_id,
          person_id:     a.person_id,
          employee_code: a.employee_code,
          fullName:      fullName || a.employee_code || a.assignment_id,
          earnings, deductions, employerCont,
          gross, totalDeductions: totalDed,
          net: gross - totalDed,
          lopDays,
          extraWorkingDays,
          holidayPay,
          proRata: workingDays > 0 ? (workingDays - lopDays + extraWorkingDays) / workingDays : 1,
          excluded,
          status: entries.length === 0 ? 'no-entries' : 'ok',
        };
      })
      .sort((a, b) => a.assignment_id.localeCompare(b.assignment_id, undefined, { numeric: true }));
  }, [assignments, elementEntries, elementMap, personMap, period, workingDays, lopByEmp, excludedEmps, varInputs, advanceSalaries, fullFinalSettlements, ffRecoveries]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(e =>
      e.fullName.toLowerCase().includes(q) ||
      e.assignment_id.toLowerCase().includes(q) ||
      (e.employee_code || '').toLowerCase().includes(q)
    );
  }, [employees, search]);

  // Totals (excluding excluded employees)
  const totals = useMemo(() => {
    const active = employees.filter(e => !e.excluded);
    return {
      count:         active.length,
      gross:         active.reduce((s, e) => s + e.gross, 0),
      deductions:    active.reduce((s, e) => s + e.totalDeductions, 0),
      employerCont:  active.reduce((s, e) => s + e.employerCont, 0),
      net:           active.reduce((s, e) => s + e.net, 0),
      totalLop:      active.reduce((s, e) => s + e.lopDays, 0),
      totalExtraWorkingDays:  active.reduce((s, e) => s + e.extraWorkingDays, 0),
      totalHolPay:   active.reduce((s, e) => s + e.holidayPay, 0),
    };
  }, [employees]);

  // ── Actions ────────────────────────────────────────────────────────────
  const buildRunPayload = (status) => ({
    run_id:                 nextRunId,
    COMPANY_id:             activeContext?.company_id || payroll?.COMPANY_id || null,
    bg_id:                  activeContext?.bg_id      || payroll?.bg_id      || null,
    ldg_id:                 activeContext?.ldg_id     || payroll?.ldg_id     || null,
    payroll_id:             period.payroll_id,
    period_id:              period.period_id,
    consolidation_group_id: payroll?.consolidation_group_id || null,
    run_type:               'Regular',
    run_name:               `${payroll?.payroll_name || period.payroll_id} ${period.period_name} Regular`,
    run_date:               new Date().toISOString().split('T')[0],
    run_status:             status,
    total_employees:        0,
    total_gross:            0,
    total_deductions:       0,
    total_employer_contrib: 0,
    total_net:              0,
    error_count:            0,
    initiated_by:           'USR001',
    effective_from:         period.start_date,
    effective_to:           '2099-12-31',
    created_by:             'SYSTEM',
  });

  const handleSaveDraft = async () => {
    if (!period) return;
    setBusy('draft');
    try {
      let finalRunId = nextRunId;
      if (!existingRun) {
        const response = await createRecord('PAYROLL_RUN', buildRunPayload('Draft'));
        finalRunId = response.data?.data?.run_id || finalRunId;
      }
      enqueueSnackbar(`Saved as Draft: ${finalRunId}`, { variant: 'success' });
      onDone?.();
    } catch (e) {
      enqueueSnackbar('Save failed: ' + (e.response?.data?.message || e.message), { variant: 'error' });
    } finally { setBusy(null); }
  };

  const handleSubmitApprove = async () => {
    if (!period) return;
    if (totals.count === 0) {
      enqueueSnackbar('No employees in scope to process', { variant: 'warning' });
      return;
    }
    setBusy('submit');
    try {
      // 1. Create run row as Pending (or use existing if resuming)
      let runIdToUse = nextRunId;
      if (!existingRun) {
        const response = await createRecord('PAYROLL_RUN', buildRunPayload('Pending'));
        runIdToUse = response.data?.data?.run_id || runIdToUse;
      }
      // 2. Build LOP overrides
      const lop_overrides = {};
      for (const [asnId, lop] of Object.entries(lopByEmp)) {
        const n = Number(lop) || 0;
        if (n > 0 && !excludedEmps.has(asnId)) lop_overrides[asnId] = n;
      }
      // 2b. Build variable inputs (Overtime / Bonus / Ad-hoc) — only non-zero entries
      const variable_inputs = {};
      for (const [asnId, v] of Object.entries(varInputs)) {
        if (excludedEmps.has(asnId)) continue;
        const ot   = Number(v?.ot_hours)         || 0;
        const otM  = Number(v?.ot_multiplier)    || 1.5;
        const bn   = Number(v?.bonus)            || 0;
        const ahE  = Number(v?.ad_hoc_earning)   || 0;
        const ahD  = Number(v?.ad_hoc_deduction) || 0;
        const exW  = Number(v?.extra_working_days) || 0;
        if (ot > 0 || bn > 0 || ahE > 0 || ahD > 0 || exW > 0) {
          variable_inputs[asnId] = {
            ot_hours: ot, ot_multiplier: otM,
            bonus: bn, ad_hoc_earning: ahE, ad_hoc_deduction: ahD,
            extra_working_days: exW,
          };
        }
      }
      // 3. Execute
      await executePayrollRun(runIdToUse, { working_days: workingDays, lop_overrides, variable_inputs });
      // 4. Approve
      await approvePayrollRun(runIdToUse);
      enqueueSnackbar(`Run ${runIdToUse} submitted, executed, and approved`, { variant: 'success' });
      onDone?.();
    } catch (e) {
      enqueueSnackbar('Submit failed: ' + (e.response?.data?.message || e.message), { variant: 'error' });
    } finally { setBusy(null); }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton height={60} sx={{ mb: 2 }} />
        {[...Array(6)].map((_, i) => <Skeleton key={i} height={56} sx={{ mb: 1 }} />)}
      </Box>
    );
  }

  if (!period) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">Period not found.</Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>Back to Pay Runs</Button>
      </Box>
    );
  }

  const applyBulkLop = () => {
    const v = Math.max(0, Math.min(workingDays, Number(bulkLop) || 0));
    const next = {};
    filteredEmployees.forEach(e => { next[e.assignment_id] = v; });
    setLopByEmp(prev => ({ ...prev, ...next }));
  };

  return (
    <Box sx={{ pb: 12 }}>{/* bottom pad for sticky footer */}
      {/* Header */}
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
        <IconButton onClick={busy ? undefined : onBack}
          sx={{ width: 36, height: 36, borderRadius: '10px',
            border: '1.5px solid #E5E7EB', color: '#374151',
            '&:hover': { borderColor: '#22c55e', color: '#16a34a', bgcolor: '#f0fdf4' } }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography fontSize={18} fontWeight={800} color="#111827">
              Processing Pay Run
            </Typography>
            <Typography fontSize={16} fontWeight={600} color="#16a34a">
              · {period.period_name}
            </Typography>
            <Chip
              icon={<AutoAwesomeIcon sx={{ fontSize: '13px !important', color: '#16a34a !important' }} />}
              label={nextRunId}
              size="small"
              sx={{ bgcolor: '#dcfce7', color: '#15803D', fontWeight: 700, fontSize: 11,
                border: '1px solid #BBF7D0' }}
            />
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography fontSize={12} color="#6B7280">
              {period.period_id} · Pay Date {period.payment_date || '—'}
            </Typography>
            <Typography fontSize={12} color="#6B7280">·</Typography>
            <Typography fontSize={12} color="#6B7280">
              {payroll?.payroll_name || period.payroll_id}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          {/* Calendar / Working Day stats */}
          <Stack direction="row" alignItems="center" gap={0.5}
            sx={{ bgcolor: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '8px', px: 1.5, py: 0.5 }}>
            <Typography fontSize={11} color="#0369A1" fontWeight={600}>Cal:</Typography>
            <Typography fontSize={13} fontWeight={800} color="#0369A1" sx={{ fontFamily: 'monospace' }}>{calendarDays}d</Typography>
          </Stack>
          {weeklyHolidayDays > 0 && (
            <Stack direction="row" alignItems="center" gap={0.5}
              sx={{ bgcolor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '8px', px: 1.5, py: 0.5 }}>
              <Typography fontSize={11} color="#C2410C" fontWeight={600}>Wk-Hols:</Typography>
              <Typography fontSize={13} fontWeight={800} color="#C2410C" sx={{ fontFamily: 'monospace' }}>{weeklyHolidayDays}d</Typography>
            </Stack>
          )}
          <Stack direction="row" alignItems="center" gap={0.5}
            sx={{ bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', px: 1.5, py: 0.5 }}>
            <Typography fontSize={11} color="#15803D" fontWeight={600}>Sched:</Typography>
            <Typography fontSize={13} fontWeight={800} color="#15803D" sx={{ fontFamily: 'monospace' }}>{scheduledDays}d</Typography>
          </Stack>
          {publicHolidayDays > 0 && (
            <Stack direction="row" alignItems="center" gap={0.5}
              sx={{ bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', px: 1.5, py: 0.5 }}>
              <Typography fontSize={11} color="#B91C1C" fontWeight={600}>Pub-Hols:</Typography>
              <Typography fontSize={13} fontWeight={800} color="#B91C1C" sx={{ fontFamily: 'monospace' }}>{publicHolidayDays}d</Typography>
            </Stack>
          )}
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography fontSize={11.5} color="text.secondary">Working days:</Typography>
            <TextField
              size="small" type="number" value={workingDays}
              onChange={e => setWorkingDays(Math.max(1, Math.min(31, Number(e.target.value) || 0)))}
              sx={{ width: 70, '& .MuiOutlinedInput-root': { bgcolor: 'white', fontSize: 13, borderRadius: '6px' },
                '& input': { textAlign: 'center', py: 0.5 } }}
              InputProps={{ inputProps: { min: 1, max: 31 } }}
            />
          </Stack>
        </Stack>
      </Stack>

      {/* Toolbar */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: '10px', border: '1.5px solid #E5E7EB',
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Search employee…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ width: 300, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '8px' } }}
          InputProps={{
            startAdornment: <InputAdornment position="start">
              <SearchIcon sx={{ color: '#94A3B8', fontSize: 17 }} />
            </InputAdornment>,
            endAdornment: search ? <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')}>
                <ClearIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </InputAdornment> : null,
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography fontSize={12} color="text.secondary">Bulk LOP:</Typography>
          <TextField
            size="small" type="number" value={bulkLop}
            onChange={e => setBulkLop(e.target.value)}
            placeholder="0"
            sx={{ width: 80, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '6px' },
              '& input': { textAlign: 'center', py: 0.5 } }}
            InputProps={{ inputProps: { min: 0, max: workingDays } }}
          />
          <Button size="small" variant="outlined" onClick={applyBulkLop}
            sx={{ fontSize: 11.5, py: 0.4, borderColor: '#22c55e', color: '#16a34a',
              '&:hover': { bgcolor: '#f0fdf4', borderColor: '#16a34a' } }}>
            Apply to visible ({filteredEmployees.length})
          </Button>
        </Stack>
      </Paper>

      {/* Employee table */}
      <Paper sx={{ borderRadius: '10px', border: '1.5px solid #E5E7EB', overflow: 'hidden' }}>
        <Stack direction="row" sx={{ px: 2, py: 1.2, bgcolor: '#F9FAFB',
          borderBottom: '1.5px solid #E5E7EB' }}>
          <Box sx={{ flex: 1.6 }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>EMPLOYEE</Typography></Box>
          <Box sx={{ width: 60, textAlign: 'center' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>WORKED</Typography></Box>
          <Box sx={{ width: 80, textAlign: 'center' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>LOP</Typography></Box>
          {weeklyHolidayDays > 0 && (
            <Box sx={{ width: 70, textAlign: 'center' }}><Typography fontSize={10.5} fontWeight={700} color="#C2410C" sx={{ letterSpacing: 0.5 }}>EXT.WRK</Typography></Box>
          )}
          <Box sx={{ width: 100, textAlign: 'right' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>GROSS</Typography></Box>
          <Box sx={{ width: 100, textAlign: 'right' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>DEDUCTIONS</Typography></Box>
          <Box sx={{ width: 110, textAlign: 'right' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>NET PAY</Typography></Box>
          <Box sx={{ width: 70, textAlign: 'center' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>STATUS</Typography></Box>
          <Box sx={{ width: 30 }} />
        </Stack>

        {filteredEmployees.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography fontSize={13} color="text.secondary">
              {employees.length === 0
                ? `No active employees found for payroll ${period.payroll_id}`
                : 'No matching employees'}
            </Typography>
          </Box>
        ) : (
          filteredEmployees.map((emp, i) => {
            const isExpanded = expandedEmp === emp.assignment_id;
            const rowBg = emp.excluded ? '#F9FAFB' : isExpanded ? '#F0FDF4' : 'white';
            const rateColor = emp.proRata === 1 ? '#16a34a'
                            : emp.proRata >= 0.8 ? '#92400E' : '#DC2626';
            return (
              <React.Fragment key={emp.assignment_id}>
                <Stack direction="row" alignItems="center"
                  sx={{ px: 2, py: 1.2, bgcolor: rowBg,
                    borderBottom: i === filteredEmployees.length - 1 && !isExpanded ? 'none' : '1px solid #F3F4F6',
                    opacity: emp.excluded ? 0.6 : 1,
                    transition: 'background 0.12s' }}>
                  <Box sx={{ flex: 1.6, minWidth: 0 }}>
                    <Typography fontSize={13} fontWeight={600} color="#111827" noWrap>
                      {emp.fullName}
                    </Typography>
                    <Stack direction="row" gap={0.6} alignItems="center">
                      <Typography fontSize={10.5} color="#9CA3AF" sx={{ fontFamily: 'monospace' }}>
                        {emp.assignment_id}
                      </Typography>
                      {emp.employee_code && <>
                        <Typography fontSize={10.5} color="#9CA3AF">·</Typography>
                        <Typography fontSize={10.5} color="#6B7280" sx={{ fontFamily: 'monospace' }}>
                          {emp.employee_code}
                        </Typography>
                      </>}
                    </Stack>
                  </Box>
                  <Box sx={{ width: 60, textAlign: 'center' }}>
                    <Typography fontSize={12.5} fontWeight={600}
                      color={rateColor} sx={{ fontFamily: 'monospace' }}>
                      {workingDays - emp.lopDays + emp.extraWorkingDays}/{workingDays}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 80, textAlign: 'center' }}>
                    <TextField
                      size="small" type="number" value={emp.lopDays || ''}
                      placeholder="0"
                      onChange={e => {
                        const v = Math.max(0, Math.min(workingDays, Number(e.target.value) || 0));
                        setLopByEmp(prev => ({ ...prev, [emp.assignment_id]: v }));
                      }}
                      sx={{ width: 60, '& .MuiOutlinedInput-root': { fontSize: 12.5, borderRadius: '6px' },
                        '& input': { textAlign: 'center', py: 0.4 } }}
                      InputProps={{ inputProps: { min: 0, max: workingDays } }}
                    />
                  </Box>
                  {weeklyHolidayDays > 0 && (
                    <Box sx={{ width: 70, textAlign: 'center' }}>
                      <TextField
                        size="small" type="number" value={(varInputs[emp.assignment_id]?.extra_working_days ?? emp.extraWorkingDays)?.toString()}
                        placeholder="0"
                        onChange={e => setVarField(emp.assignment_id, 'extra_working_days', e.target.value === '' ? 0 : Number(e.target.value))}
                        sx={{ width: 55, '& .MuiOutlinedInput-root': { fontSize: 12.5, borderRadius: '6px', borderColor: '#FED7AA' },
                          '& input': { textAlign: 'center', py: 0.4, color: '#C2410C' } }}
                        InputProps={{ inputProps: { min: 0, max: weeklyHolidayDays } }}
                      />
                    </Box>
                  )}
                  <Box sx={{ width: 100, textAlign: 'right' }}>
                    <Typography fontSize={12.5} fontWeight={600} color="#111827" sx={{ fontFamily: 'monospace' }}>
                      ₹{fmtINR(emp.gross)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 100, textAlign: 'right' }}>
                    <Typography fontSize={12.5} fontWeight={600} color="#DC2626" sx={{ fontFamily: 'monospace' }}>
                      −₹{fmtINR(emp.totalDeductions)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 110, textAlign: 'right' }}>
                    <Typography fontSize={13.5} fontWeight={800} color="#16a34a" sx={{ fontFamily: 'monospace' }}>
                      ₹{fmtINR(emp.net)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 70, textAlign: 'center' }}>
                    {emp.status === 'no-entries' ? (
                      <Chip label="No Entries" size="small"
                        sx={{ height: 18, fontSize: 9.5, fontWeight: 600,
                          bgcolor: '#FEF3C7', color: '#92400E' }} />
                    ) : (
                      <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: '11px !important' }} />}
                        label="OK" size="small"
                        sx={{ height: 18, fontSize: 9.5, fontWeight: 600,
                          bgcolor: '#DCFCE7', color: '#15803D' }} />
                    )}
                  </Box>
                  <Box sx={{ width: 30, display: 'flex', justifyContent: 'center' }}>
                    {(emp.earnings.length + emp.deductions.length) > 0 && (
                      <IconButton size="small"
                        onClick={() => setExpandedEmp(isExpanded ? null : emp.assignment_id)}
                        sx={{ p: 0.4, color: '#6B7280' }}>
                        {isExpanded
                          ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                          : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    )}
                  </Box>
                </Stack>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 3, py: 1.5, bgcolor: '#FAFBFC',
                    borderBottom: i === filteredEmployees.length - 1 ? 'none' : '1px solid #F3F4F6' }}>
                    {/* Phase 6: Variable Inputs row (Overtime, Bonus, Ad-hoc) */}
                    <Box sx={{
                      mb: 1.5, p: 1.5, bgcolor: '#FFFBEB',
                      border: '1px dashed #FBBF24', borderRadius: '8px',
                    }}>
                      <Typography fontSize={11} fontWeight={700} color="#A16207"
                        sx={{ letterSpacing: 0.5, mb: 1 }}>
                        VARIABLE INPUTS — applied on top of standard pay (no LOP pro-rate)
                      </Typography>
                      <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
                        <TextField
                          size="small" type="number"
                          label="OT Hours"
                          value={varInputs[emp.assignment_id]?.ot_hours ?? ''}
                          onChange={(e) => setVarField(emp.assignment_id, 'ot_hours', e.target.value)}
                          inputProps={{ min: 0, max: 200, step: 0.5 }}
                          sx={{ width: 100 }}
                        />
                        <TextField
                          size="small" type="number"
                          label="OT × rate"
                          value={varInputs[emp.assignment_id]?.ot_multiplier ?? 1.5}
                          onChange={(e) => setVarField(emp.assignment_id, 'ot_multiplier', e.target.value)}
                          inputProps={{ min: 1, max: 4, step: 0.25 }}
                          sx={{ width: 90 }}
                          helperText="1.5 / 2 / 3"
                        />
                        <TextField
                          size="small" type="number"
                          label="Bonus (₹)"
                          value={varInputs[emp.assignment_id]?.bonus ?? ''}
                          onChange={(e) => setVarField(emp.assignment_id, 'bonus', e.target.value)}
                          inputProps={{ min: 0, max: 9999999 }}
                          sx={{ width: 120 }}
                        />
                        <TextField
                          size="small" type="number"
                          label="Ad-hoc Earn (₹)"
                          value={varInputs[emp.assignment_id]?.ad_hoc_earning ?? ''}
                          onChange={(e) => setVarField(emp.assignment_id, 'ad_hoc_earning', e.target.value)}
                          inputProps={{ min: 0, max: 9999999 }}
                          sx={{ width: 140 }}
                        />
                        <TextField
                          size="small" type="number"
                          label="Ad-hoc Deduct (₹)"
                          value={varInputs[emp.assignment_id]?.ad_hoc_deduction ?? ''}
                          onChange={(e) => setVarField(emp.assignment_id, 'ad_hoc_deduction', e.target.value)}
                          inputProps={{ min: 0, max: 9999999 }}
                          sx={{ width: 150 }}
                        />
                      </Stack>
                    </Box>
                    <Stack direction="row" gap={3}>
                      {/* Earnings */}
                      <Box sx={{ flex: 1, bgcolor: 'white', borderRadius: '8px',
                        border: '1px solid #BBF7D0', overflow: 'hidden' }}>
                        <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#F0FDF4', borderBottom: '1px solid #BBF7D0' }}>
                          <Typography fontSize={11} fontWeight={700} color="#15803D"
                            sx={{ letterSpacing: 0.5 }}>EARNINGS</Typography>
                        </Box>
                        {emp.earnings.length === 0 ? (
                          <Typography fontSize={11.5} color="text.secondary" sx={{ p: 1 }}>None</Typography>
                        ) : emp.earnings.map(it => (
                          <Stack key={it.element_id} direction="row" justifyContent="space-between"
                            sx={{ px: 1.5, py: 0.5, '&:not(:last-child)': { borderBottom: '1px solid #F3F4F6' } }}>
                            <Typography fontSize={11.5} color="#374151">{it.element_name}</Typography>
                            <Stack direction="row" gap={1}>
                              {it.amount !== it.raw_amount && (
                                <Typography fontSize={10.5} color="#9CA3AF"
                                  sx={{ fontFamily: 'monospace', textDecoration: 'line-through' }}>
                                  ₹{fmtINR(it.raw_amount)}
                                </Typography>
                              )}
                              <Typography fontSize={11.5} fontWeight={600} color="#111827"
                                sx={{ fontFamily: 'monospace' }}>
                                ₹{fmtINR(it.amount)}
                              </Typography>
                            </Stack>
                          </Stack>
                        ))}
                      </Box>
                      {/* Deductions */}
                      <Box sx={{ flex: 1, bgcolor: 'white', borderRadius: '8px',
                        border: '1px solid #FECACA', overflow: 'hidden' }}>
                        <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#FEF2F2', borderBottom: '1px solid #FECACA' }}>
                          <Typography fontSize={11} fontWeight={700} color="#991B1B"
                            sx={{ letterSpacing: 0.5 }}>DEDUCTIONS</Typography>
                        </Box>
                        {emp.deductions.length === 0 ? (
                          <Typography fontSize={11.5} color="text.secondary" sx={{ p: 1 }}>None</Typography>
                        ) : emp.deductions.map(it => (
                          <Stack key={it.element_id} direction="row" justifyContent="space-between"
                            sx={{ px: 1.5, py: 0.5, '&:not(:last-child)': { borderBottom: '1px solid #F3F4F6' } }}>
                            <Typography fontSize={11.5} color="#374151">{it.element_name}</Typography>
                            <Typography fontSize={11.5} fontWeight={600} color="#DC2626"
                              sx={{ fontFamily: 'monospace' }}>
                              −₹{fmtINR(Math.abs(it.amount))}
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    </Stack>
                  </Box>
                </Collapse>
              </React.Fragment>
            );
          })
        )}
      </Paper>

      {/* Sticky footer with totals + actions */}
      <Paper sx={{
        position: 'fixed', bottom: 0, left: 260, right: 0,  // 260 = sidebar width
        bgcolor: 'white', borderTop: '2px solid #16a34a',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
        p: 1.5, zIndex: 10,
      }}>
        <Stack direction="row" alignItems="center" gap={3}>
          <Stack direction="row" alignItems="center" gap={1}>
            <GroupsIcon sx={{ fontSize: 20, color: '#16a34a' }} />
            <Box>
              <Typography fontSize={10.5} fontWeight={700} color="#9CA3AF"
                sx={{ letterSpacing: 0.5 }}>EMPLOYEES</Typography>
              <Typography fontSize={15} fontWeight={800} color="#111827">{totals.count}</Typography>
            </Box>
          </Stack>
          {totals.totalLop > 0 && (
            <Box>
              <Typography fontSize={10.5} fontWeight={700} color="#9CA3AF" sx={{ letterSpacing: 0.5 }}>TOTAL LOP</Typography>
              <Typography fontSize={15} fontWeight={800} color="#92400E">{totals.totalLop} days</Typography>
            </Box>
          )}
          {totals.totalHolWrkd > 0 && (
            <Box>
              <Typography fontSize={10.5} fontWeight={700} color="#9CA3AF" sx={{ letterSpacing: 0.5 }}>HOL. WORKED</Typography>
              <Typography fontSize={15} fontWeight={800} color="#C2410C">{totals.totalHolWrkd}d · ₹{fmtCompact(totals.totalHolPay)}</Typography>
            </Box>
          )}
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography fontSize={10.5} fontWeight={700} color="#9CA3AF" sx={{ letterSpacing: 0.5 }}>GROSS</Typography>
            <Typography fontSize={15} fontWeight={800} color="#111827" sx={{ fontFamily: 'monospace' }}>
              ₹{fmtCompact(totals.gross)}
            </Typography>
          </Box>
          <Box>
            <Typography fontSize={10.5} fontWeight={700} color="#9CA3AF" sx={{ letterSpacing: 0.5 }}>DEDUCTIONS</Typography>
            <Typography fontSize={15} fontWeight={800} color="#DC2626" sx={{ fontFamily: 'monospace' }}>
              −₹{fmtCompact(totals.deductions)}
            </Typography>
          </Box>
          <Box>
            <Typography fontSize={10.5} fontWeight={700} color="#9CA3AF" sx={{ letterSpacing: 0.5 }}>EMPLOYER</Typography>
            <Typography fontSize={15} fontWeight={800} color="#1D4ED8" sx={{ fontFamily: 'monospace' }}>
              ₹{fmtCompact(totals.employerCont)}
            </Typography>
          </Box>
          <Box>
            <Typography fontSize={10.5} fontWeight={700} color="#16a34a" sx={{ letterSpacing: 0.5 }}>NET PAY</Typography>
            <Typography fontSize={18} fontWeight={900} color="#16a34a" sx={{ fontFamily: 'monospace' }}>
              ₹{fmtCompact(totals.net)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />

          <Button variant="outlined" size="small" disabled={!!busy}
            startIcon={<SaveOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={handleSaveDraft}
            sx={{ borderColor: '#22c55e', color: '#16a34a',
              '&:hover': { bgcolor: '#f0fdf4', borderColor: '#16a34a' } }}>
            {busy === 'draft' ? 'Saving…' : 'Save as Draft'}
          </Button>
          <Button variant="contained" disabled={!!busy || totals.count === 0}
            startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
            onClick={handleSubmitApprove}
            sx={{
              background: 'linear-gradient(135deg,#16a34a,#22c55e)',
              '&:hover': { background: 'linear-gradient(135deg,#15803d,#16a34a)' },
              fontWeight: 800, px: 3,
            }}>
            {busy === 'submit' ? 'Processing…' : 'Submit & Approve'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
